import Banner from '@/components/UI/Banner';
import Button from '@/components/UI/Button';
import { _isNotEmpty, _notNil } from '@/littledash';
import type { Finding, SendValidationResult, Severity } from '@/model/Agent.model';
import { useCallback, useMemo, useRef, useState, type FC } from 'react';
import SendCellValue from './SendCellValue';
import SendFindingSource from './SendFindingSource';
import SendOmissionNotice from './SendOmissionNotice';
import SendPartialCheck from './SendPartialCheck';
import SendSeverityBadge from './SendSeverityBadge';
import { aiNotesLast, IN_VIVO_AI, omissionOf } from './sendReport';
import { sendDomainLabel } from './sendDomains';
import type { SendFileRuleCheck } from './sendRuleOutcomes';
import styles from './sendResults.module.scss';

const PAGE_SIZE = 25;

interface SendResultsGridProps {
  result: SendValidationResult;
  /** What the rules made of this file, from the server's tally; null on results stored before it. */
  ruleCheck?: SendFileRuleCheck | null;
  /** Rules whose check covers only part of the rule, so their findings carry that label. */
  proxyRules?: ReadonlySet<string>;
  testId?: string;
}

const plural = (count: number, singular: string, pluralForm: string): string =>
  `${count} ${count === 1 ? singular : pluralForm}`;

/**
 * The headline for a file nothing was found in. It says the file passed validation only when every
 * rule for it finished with an exact pass and every check decided everything it read; otherwise it
 * says what was not checked, so a partial or missing check never reads as a pass.
 */
const noIssuesHeadline = (domainCode: string, aiNotes: number, ruleCheck?: SendFileRuleCheck | null): string => {
  const aiNotesToReview = `${plural(aiNotes, `${IN_VIVO_AI} note`, `${IN_VIVO_AI} notes`)} to review`;
  if (ruleCheck?.exactPass === true) {
    return aiNotes > 0
      ? `No issues found by the validation checks — ${aiNotesToReview}`
      : `No issues found — ${sendDomainLabel(domainCode)} passed validation`;
  }
  const unchecked = _notNil(ruleCheck)
    ? [
        { count: ruleCheck.notEvaluated, text: plural(ruleCheck.notEvaluated, 'rule', 'rules') + ' not evaluated' },
        {
          count: ruleCheck.checkedPartially,
          text: plural(ruleCheck.checkedPartially, 'rule', 'rules') + ' checked partially',
        },
        {
          count: ruleCheck.undecided,
          text: plural(ruleCheck.undecided, 'check', 'checks') + ' could not decide everything',
        },
      ].reduce<Array<string>>((texts, { count, text }) => {
        if (count > 0) {
          texts.push(text);
        }
        return texts;
      }, [])
    : [];
  const details = aiNotes > 0 ? [...unchecked, aiNotesToReview] : unchecked;
  return _isNotEmpty(details)
    ? `No issues found in the checks that ran — ${details.join(', ')}`
    : 'No issues found in the checks that ran';
};

const severityRank: Record<Severity, number> = { error: 0, warning: 1, info: 2 };

const severityLabel: Record<Severity, string> = { error: 'Errors', warning: 'Warnings', info: 'Infos' };

const severityCountClass: Record<Severity, string> = {
  error: styles.badgeError,
  warning: styles.badgeWarning,
  info: styles.badgeInfo,
};

const humanize = (value: string): string => value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

const bySeverityThenRow = (a: Finding, b: Finding): number => {
  const severityDelta = severityRank[a.severity] - severityRank[b.severity];
  if (severityDelta !== 0) {
    return severityDelta;
  }
  return (a.row_number ?? 0) - (b.row_number ?? 0);
};

/**
 * Builds a React key for a finding. Findings carry no server id, so the key combines the finding's
 * identifying fields (category, column, row) with the absolute list index — the index disambiguates
 * findings whose other fields coincide (e.g. two column-level findings with null row/column).
 */
const findingKey = (finding: Finding, index: number): string =>
  `${finding.category}-${finding.column_name ?? ''}-${finding.row_number ?? ''}-${index}`;

/**
 * Results for one file of a SEND validation run: a headline banner keyed on the submission-blocking
 * count, severity chips, a jump-to-row list and a client-paginated grid of the findings the result
 * carries. Headlines read the exact counts, not the carried list, and the omission notice announces
 * whatever a limit left out.
 */
const SendResultsGrid: FC<SendResultsGridProps> = ({
  result,
  ruleCheck,
  proxyRules = new Set<string>(),
  testId = 'invivo-send-validator__results',
}) => {
  const { summary, findings, counts, advisory, domain_code: domainCode, row_count: rowCount } = result;
  const blocking = summary.submission_blocking_count;
  // A rule can fail for a file with the finding reported beside another dataset's, as a cross-domain
  // check does, so the file's own findings alone cannot say it had no issues.
  const rulesFailed = ruleCheck?.failed ?? 0;
  const hasIssues = _isNotEmpty(findings);
  // What the checks found, not what the result carries: a capped file must not understate itself here.
  const foundCount = counts.finding_groups;
  // Every AI note, shown or not. They are advisory, so they never turn the file into a failure, but a
  // success headline must still say they are there to read.
  const aiNotes = advisory.observations;

  const [page, setPage] = useState(0);

  const isByProxy = (finding: Finding): boolean => _notNil(finding.rule) && proxyRules.has(finding.rule.id);

  const sortedFindings = useMemo(() => [...findings].sort(aiNotesLast(bySeverityThenRow)), [findings]);

  const pageCount = Math.max(1, Math.ceil(sortedFindings.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageFindings = sortedFindings.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  // Grid rows keyed by their absolute index in sortedFindings, so the jump-to-row list can scroll to
  // and highlight the matching row (turning the same page first when the target is paginated away).
  const rowRefs = useRef(new Map<number, HTMLTableRowElement>());

  const jumpToFinding = useCallback(
    (absoluteIndex: number): void => {
      setPage(Math.floor(absoluteIndex / PAGE_SIZE));
      // The row for a different page mounts after this render, so defer the scroll a frame.
      requestAnimationFrame(() => {
        const row = rowRefs.current.get(absoluteIndex);
        if (_notNil(row)) {
          row.scrollIntoView({ block: 'center' });
          row.focus();
        }
      });
    },
    [setPage]
  );

  const severityChips: Array<{ severity: Severity; count: number }> = [
    { severity: 'error', count: summary.error_count },
    { severity: 'warning', count: summary.warning_count },
    { severity: 'info', count: summary.info_count },
  ];

  return (
    <div
      className="ui-card pa4"
      data-test-component="SendResultsGrid"
      data-test-element="container"
      data-testid={testId}
    >
      <h3 className={`${styles.sectionTitle} mb3`} data-testid="invivo-send-validator__results-meta">
        {sendDomainLabel(domainCode)}{' '}
        <span className={styles.sectionMeta}>
          · {rowCount} {rowCount === 1 ? 'row' : 'rows'}
        </span>
      </h3>

      {blocking > 0 ? (
        <Banner critical dismiss={false} testId="invivo-send-validator__results-banner">
          <p className="ma0 fw5" data-testid="invivo-send-validator__results-headline">
            {blocking} submission-blocking {blocking === 1 ? 'issue' : 'issues'} found
          </p>
        </Banner>
      ) : foundCount > 0 ? (
        <Banner warning dismiss={false} testId="invivo-send-validator__results-banner">
          <p className="ma0 fw5" data-testid="invivo-send-validator__results-headline">
            No submission-blocking issues — {foundCount} non-blocking {foundCount === 1 ? 'finding' : 'findings'} to
            review
          </p>
        </Banner>
      ) : rulesFailed > 0 ? (
        <Banner warning dismiss={false} testId="invivo-send-validator__results-banner">
          <p className="ma0 fw5" data-testid="invivo-send-validator__results-headline">
            {plural(rulesFailed, 'rule', 'rules')} failed for {sendDomainLabel(domainCode)} — the findings are in the
            cross-domain checks
          </p>
        </Banner>
      ) : (
        <Banner
          success={ruleCheck?.exactPass === true}
          info={ruleCheck?.exactPass !== true}
          dismiss={false}
          testId="invivo-send-validator__results-banner"
        >
          <p className="ma0 fw5" data-testid="invivo-send-validator__results-headline">
            {noIssuesHeadline(domainCode, aiNotes, ruleCheck)}
          </p>
        </Banner>
      )}

      <div className="flex items-center mt3" data-test-element="summary">
        <div className="flex items-center" data-test-element="severity-chips">
          {severityChips.map(({ severity, count }) => (
            <span
              key={severity}
              className={`${styles.badge} ${severityCountClass[severity]} mr2`}
              data-test-element="severity-chip"
              data-test-key={severity}
            >
              {severityLabel[severity]} <span className="fw6">{count}</span>
            </span>
          ))}
        </div>
      </div>

      <SendOmissionNotice omission={omissionOf(counts, advisory)} testId="invivo-send-validator__omission" />

      {hasIssues && (
        <div className="mt3" data-test-element="jump-to-row" data-testid="invivo-send-validator__jump-list">
          <h4 className="f6 fw6 near-black ma0 mb2">Findings</h4>
          <ul className={styles.issueList}>
            {sortedFindings.slice(0, 10).map((finding, index) => (
              <li key={findingKey(finding, index)}>
                <button
                  type="button"
                  className={styles.issueItem}
                  data-test-element="jump-to-row-item"
                  onClick={() => jumpToFinding(index)}
                >
                  <span className={`${styles.severityDot} ${styles[finding.severity]}`} aria-hidden="true" />
                  <SendFindingSource finding={finding} className="mr2" />
                  {isByProxy(finding) && <SendPartialCheck className="mr2" />}
                  {_notNil(finding.row_number) ? `Row ${finding.row_number}: ` : ''}
                  {finding.message}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {hasIssues && (
        <div className="mt3" data-test-element="findings-grid">
          <table className={styles.grid} data-testid="invivo-send-validator__grid">
            <thead>
              <tr>
                <th>Severity</th>
                <th>Source</th>
                <th>Rule</th>
                <th>Category</th>
                <th>Column</th>
                <th>Row</th>
                <th>Current</th>
                <th>Expected</th>
                <th>Message</th>
              </tr>
            </thead>
            <tbody>
              {pageFindings.map((finding: Finding, index) => {
                const absoluteIndex = safePage * PAGE_SIZE + index;
                return (
                  <tr
                    key={findingKey(finding, absoluteIndex)}
                    ref={(node) => {
                      if (_notNil(node)) {
                        rowRefs.current.set(absoluteIndex, node);
                      } else {
                        rowRefs.current.delete(absoluteIndex);
                      }
                    }}
                    tabIndex={-1}
                    data-test-element="finding-row"
                  >
                    <td>
                      <SendSeverityBadge severity={finding.severity} />
                    </td>
                    <td>
                      <SendFindingSource finding={finding} />
                    </td>
                    <td className={styles.nowrap} data-test-element="finding-rule">
                      {finding.rule?.id ?? '—'}
                      {isByProxy(finding) && <SendPartialCheck className="ml2" />}
                    </td>
                    <td>{humanize(finding.category)}</td>
                    <td>{finding.column_name ?? '—'}</td>
                    <td>{_notNil(finding.row_number) ? finding.row_number : '—'}</td>
                    <td data-test-element="finding-current">
                      {_notNil(finding.current_value) ? <SendCellValue value={finding.current_value} /> : '—'}
                    </td>
                    <td data-test-element="finding-expected">
                      {_notNil(finding.expected_value) ? <SendCellValue value={finding.expected_value} /> : '—'}
                    </td>
                    <td>{finding.message}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {pageCount > 1 && (
            <div className="flex items-center mt3" data-test-element="pagination">
              <Button
                outline
                disabled={safePage === 0}
                onClick={() => setPage(safePage - 1)}
                testId="invivo-send-validator__grid-prev"
              >
                Previous
              </Button>
              <span className="f6 dark-gray mh3" data-testid="invivo-send-validator__grid-page">
                Page {safePage + 1} of {pageCount}
              </span>
              <Button
                outline
                disabled={safePage >= pageCount - 1}
                onClick={() => setPage(safePage + 1)}
                testId="invivo-send-validator__grid-next"
              >
                Next
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SendResultsGrid;
